import { Category, MenuItem } from '@/types/common';
import { getCategoryIcon } from '@/utils/menuIcons';

interface MenuSectionProps {
  category: Category;
  items: MenuItem[];
  editable?: boolean;
  showPrices?: boolean;
  actionLabel?: string;
  onItemClick: (item: MenuItem) => void;
  onUpdateItem?: (id: string, data: Omit<MenuItem, 'id' | 'created_at'>) => Promise<void>;
  onEditItem?: (item: MenuItem) => void;
  onDeleteItem?: (id: string) => Promise<void>;
}

export default function MenuSection({
  category,
  items,
  editable = false,
  showPrices = true,
  actionLabel = 'Select',
  onItemClick,
  onEditItem,
  onDeleteItem
}: MenuSectionProps) {
  console.log('MenuSection rendering:', {
    category: category.name,
    items: items.map(i => ({
      id: i.id,
      name: i.name,
      hasCategory: !!i.category,
      categoryMatches: i.category?.id === category.id
    }))
  });

  return (
    <div className="space-y-4">
      {/* Category Header */}
      <div className="flex items-center">
        <i className={`${getCategoryIcon(category.name)} text-xl mr-3 text-gray-600 dark:text-dark-text-secondary`} />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-text-primary">
          {category.name}
        </h2>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((item) => (
          <div key={item.id} className="relative group">
            <div className="p-4 bg-white/90 dark:bg-dark-bg-light/90 backdrop-blur-sm rounded-lg shadow-sm hover:shadow-md transition-shadow border border-gray-100 dark:border-dark-border">
              <div className="flex items-center mb-2">
                <i className={`${getCategoryIcon(category.name)} mr-2 text-gray-500 dark:text-dark-text-secondary`} />
                <h3 className="font-medium text-lg text-gray-900 dark:text-dark-text-primary">
                  {item.name}
                </h3>
              </div>
              {/* Status Badge */}
              {!item.active && (
                <span className="absolute top-2 right-2 px-2 py-1 text-xs font-medium text-gray-600 dark:text-dark-text-secondary bg-gray-100 dark:bg-dark-bg rounded">
                  Unavailable
                </span>
              )}

              {/* Content */}
              <div className="mb-4">
                {item.description && (
                  <p className="text-sm text-gray-600 dark:text-dark-text-secondary">
                    {item.description}
                  </p>
                )}
              </div>

              {/* Subcategory Tag */}
              {item.subcategory && (
                <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-400 mb-2">
                  {item.subcategory.name}
                </span>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between mt-2">
                {showPrices && (
                  <span className="text-sm font-medium text-gray-900 dark:text-dark-text-primary">
                    €{item.price.toFixed(2)}
                  </span>
                )}
                <div className="flex gap-2">
                  {editable && (
                    <>
                      <button
                        onClick={() => onEditItem?.(item)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg-lighter"
                      >
                        <i className="fas fa-pencil-alt" />
                      </button>
                      <button
                        onClick={() => onDeleteItem?.(item.id)}
                        className="p-1.5 rounded-md text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-gray-100 dark:hover:bg-dark-bg-lighter"
                      >
                        <i className="fas fa-trash" />
                      </button>
                    </>
                  )}
                  {!editable && (
                    <button
                      onClick={() => onItemClick(item)}
                      className="flex items-center px-3 py-1.5 text-sm font-medium rounded-md text-primary-600 dark:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30"
                    >
                      {actionLabel}
                      <i className="fas fa-chevron-right ml-1.5 text-xs" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}